import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Clock, CheckCircle2, Mail, ShieldCheck } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  usePortalTeamMembers,
  useRemoveTeamMember,
  usePortalMe,
} from '@/api/hooks/usePortal';

export function PortalTeamPage() {
  const { data: members, isLoading } = usePortalTeamMembers();
  const { data: me } = usePortalMe();
  const removeTeamMember = useRemoveTeamMember();

  // isAdmin = I am the first accepted member (admin)
  const isAdmin = !!members?.find((m) => m.is_admin && m.email === me?.email);

  async function handleRemove(contactId: string, email: string) {
    if (!confirm(`Remove ${email} from the portal? They will lose access immediately.`)) return;
    try {
      await removeTeamMember.mutateAsync(contactId);
      toast.success(`${email} has been removed`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: { message?: string } } } })
        ?.response?.data?.errors?.message;
      toast.error(msg ?? 'Failed to remove member');
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-text-secondary" />
          <h1 className="text-lg font-bold text-text-primary">Team</h1>
        </div>
        {isAdmin && (
          <span title="Team member invitations are temporarily unavailable">
            <Button size="sm" disabled>
              <UserPlus className="h-4 w-4 mr-1.5" />Invite
            </Button>
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
        </div>
      ) : (
        <div className="divide-y divide-border-subtle rounded-lg border border-border-subtle bg-bg-surface">
          {(members ?? []).map((member) => {
            const isMe = member.email === me?.email;
            return (
              <div key={member.id} className="flex items-center gap-3 px-4 py-3">
                {/* Avatar */}
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-surface-2 text-sm font-medium text-text-secondary">
                  {(member.first_name?.[0] ?? member.email[0]).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}
                    </p>
                    {member.is_admin && (
                      <Badge variant="default" className="text-[10px] gap-0.5">
                        <ShieldCheck className="h-3 w-3" />Admin
                      </Badge>
                    )}
                    {isMe && <Badge variant="muted" className="text-[10px]">You</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-xs text-text-secondary truncate">{member.email}</p>
                    {member.role_title && (
                      <p className="text-xs text-text-secondary hidden sm:block">· {member.role_title}</p>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="shrink-0 text-right hidden sm:block">
                  {member.status === 'pending' ? (
                    <div className="flex items-center gap-1 text-xs text-text-secondary">
                      <Mail className="h-3.5 w-3.5" />Invited
                      {member.invitation_sent_at && (
                        <> {formatDistanceToNow(parseISO(member.invitation_sent_at), { addSuffix: true })}</>
                      )}
                    </div>
                  ) : member.last_login_at ? (
                    <div className="flex items-center gap-1 text-xs text-text-secondary">
                      <CheckCircle2 className="h-3.5 w-3.5 text-accent-emerald" />
                      Last seen {formatDistanceToNow(parseISO(member.last_login_at), { addSuffix: true })}
                    </div>
                  ) : member.accepted_at ? (
                    <div className="flex items-center gap-1 text-xs text-text-secondary">
                      <Clock className="h-3.5 w-3.5" />Never logged in
                    </div>
                  ) : null}
                </div>

                {/* Remove */}
                {isAdmin && !isMe && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-text-secondary hover:text-accent-rose"
                    onClick={() => handleRemove(member.id, member.email)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isAdmin && (
        <p className="text-xs text-text-secondary">
          Contact the portal admin to invite or remove team members.
        </p>
      )}
    </div>
  );
}
